import { useId, useMemo } from 'react';
import { Link } from 'react-router';
import { AlertCircle } from 'lucide-react';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { JobRowCard } from './JobRowCard';
import { JobRowCardSkeleton } from './JobRowCardSkeleton';
import type { JobListDTO } from '@/types/db';

/**
 * `<JobsList>` — UI-SPEC § 3.4 list page main content.
 *
 * Renders five distinct visual states from a single component:
 * 1. `isError`                          → glass-strong error panel + retry
 * 2. `isLoading`                        → 3 JobRowCardSkeletons (aria-busy)
 * 3. cold-start (no jobs at all)        → glass-strong empty state + CTA
 * 4. search filtered to 0               → "No matches" + clear-search button
 * 5. populated                          → SR-only result count + card stream
 *
 * Search:
 * - Client-side substring filter on `address` (case-insensitive).
 * - Result count announced via `aria-live="polite"` (sr-only).
 * - The Input is fully controlled by the parent route (Plan 07-03 mounts).
 *
 * Bronze discipline: this component intentionally avoids the bronze
 * accent color for the CTA — the user's locked decision is "Bronze NOT
 * in jobs components" (Phase 05 reserved-list preserved). Focus-visible
 * rings (slot #7) remain.
 */
export interface JobsListProps {
  jobs: JobListDTO[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry?: () => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
}

export function JobsList({
  jobs,
  isLoading,
  isError,
  onRetry,
  searchQuery,
  onSearchChange,
}: JobsListProps) {
  const searchInputId = useId();

  const filtered = useMemo(() => {
    if (!jobs) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((j) => j.address.toLowerCase().includes(q));
  }, [jobs, searchQuery]);

  return (
    <section aria-label="Jobs list" className="flex flex-col gap-6">
      <header
        className={cn(
          'flex flex-col gap-3',
          'md:flex-row md:justify-between md:items-center md:gap-4 md:flex-wrap',
        )}
      >
        <h1 className="text-xl font-semibold text-foam-50">Jobs</h1>
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:gap-4">
          <Input
            id={searchInputId}
            type="search"
            placeholder="Search address…"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search jobs by address"
            className="md:max-w-[320px]"
          />
          <Link
            to="/submit"
            className={cn(
              'text-sm font-semibold text-foam-50 bg-foam-50/10 hover:bg-foam-50/15',
              'px-3 h-9 inline-flex items-center justify-center rounded-lg',
              'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
            )}
          >
            + Submit New Address
          </Link>
        </div>
      </header>

      {isError ? (
        <GlassPanel
          variant="strong"
          className="p-12 flex items-start gap-4"
          role="alert"
        >
          <AlertCircle
            className="w-6 h-6 text-coral-500 shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div className="flex flex-col gap-2">
            <h2 className="text-base font-semibold text-foam-50">
              Couldn&apos;t load jobs.
            </h2>
            <p className="text-sm text-foam-50/80">
              The server returned an error. Try reloading the page.
            </p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className={cn(
                  'self-start text-sm font-semibold text-tide-400 hover:text-tide-400/80',
                  'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded',
                )}
              >
                Retry
              </button>
            ) : null}
          </div>
        </GlassPanel>
      ) : isLoading ? (
        <div role="list" aria-busy="true" className="flex flex-col gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <JobRowCardSkeleton key={i} />
          ))}
        </div>
      ) : filtered.length === 0 && (jobs?.length ?? 0) === 0 ? (
        // Cold-start empty
        <GlassPanel
          variant="strong"
          className="p-12 text-center flex flex-col items-center gap-4"
        >
          <h2 className="text-xl font-semibold text-foam-50">No jobs yet.</h2>
          <p className="text-base text-foam-50/70">
            Submit an address to launch the research agents.
          </p>
          <Link
            to="/submit"
            className={cn(
              'text-sm font-semibold text-foam-50 bg-foam-50/15 hover:bg-foam-50/20',
              'px-6 h-12 inline-flex items-center rounded-2xl',
              'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
            )}
          >
            Submit your first address
          </Link>
        </GlassPanel>
      ) : filtered.length === 0 ? (
        // Search filtered to 0
        <div className="text-center flex flex-col gap-3 py-6">
          <p className="text-base text-foam-50/70">
            No jobs match &ldquo;{searchQuery}&rdquo;. Try a different search.
          </p>
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className={cn(
              'self-center text-sm font-semibold text-tide-400 hover:text-tide-400/80',
              'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded',
            )}
          >
            Clear search
          </button>
        </div>
      ) : (
        <>
          <div className="sr-only" aria-live="polite">
            {filtered.length} of {jobs?.length ?? 0} jobs
          </div>
          <div role="list" className="flex flex-col gap-4">
            {filtered.map((j) => (
              <JobRowCard key={j.job_id} job={j} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
